import { useEffect, useRef, useState } from "react";
import { ApiError, requestOtp, verifyOtp } from "../engine/useRun";
import { useSession } from "./session";
import { Banner, Button, Dialog } from "./primitives";
import { TextInput } from "./controls";
import { Icon } from "./Icon";

/**
 * Phone + OTP sheet. Two steps: enter phone → enter the 6-digit code.
 *
 * The OTP channel is mock at launch (the code comes back in the response), so
 * the app is testable with no SMS contract. We surface that dev code inline
 * when the server provides it — clearly labelled as a dev convenience.
 *
 * NOTE: this is the ONE place the app takes a phone number. It is not a
 * credential we store or transmit anywhere except the auth endpoint the user's
 * own backend owns.
 */

type Step = "phone" | "code";

// Iraqi mobile: 07XXXXXXXXX (11 digits) or +9647XXXXXXXXX.
function normalizePhone(raw: string): string {
  return raw.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660)).replace(/[^\d+]/g, "");
}
function isValidIraqiPhone(p: string): boolean {
  const n = normalizePhone(p);
  return /^07\d{9}$/.test(n) || /^\+9647\d{9}$/.test(n) || /^009647\d{9}$/.test(n);
}

export function AuthDialog() {
  const { authOpen, authReason, closeAuth, setSession } = useSession();
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  // Reset to a clean first step every time the sheet opens.
  useEffect(() => {
    if (authOpen) {
      setStep("phone");
      setCode("");
      setDevCode(null);
      setError(null);
      setBusy(false);
    }
  }, [authOpen]);

  useEffect(() => {
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  async function submitPhone(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!isValidIraqiPhone(phone)) {
      setError("اكتب رقم عراقي صحيح، مثال: 07701234567");
      return;
    }
    setBusy(true);
    try {
      const res = await requestOtp(normalizePhone(phone));
      setDevCode(res.devCode ?? null);
      setStep("code");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ما كدرنا نرسل الرمز. جرّب مرة ثانية.");
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const c = normalizePhone(code);
    if (!/^\d{4,8}$/.test(c)) {
      setError("اكتب الرمز اللي وصلك.");
      return;
    }
    setBusy(true);
    try {
      const session = await verifyOtp(normalizePhone(phone), c);
      if (!session.user) {
        setError("الرمز غير صحيح أو انتهت صلاحيته.");
        return;
      }
      setSession(session);
      closeAuth();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "الرمز غير صحيح. جرّب مرة ثانية.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={authOpen}
      onClose={closeAuth}
      title={step === "phone" ? "سجّل دخول" : "أدخل الرمز"}
    >
      {authReason ? (
        <Banner tone="primary" icon="info" className="mb-4">
          {authReason}
        </Banner>
      ) : null}

      {step === "phone" ? (
        <form onSubmit={submitPhone} className="grid gap-4" noValidate>
          <p className="md-body-medium text-on-surface-variant">
            حسابك يخليك تكمّل تشغيلات أكثر وتحفظ تاريخك. راح نرسل لك رمز تأكيد على رقمك.
          </p>
          <div>
            <label htmlFor="auth-phone" className="md-title-small mb-1 block">
              رقم الموبايل
            </label>
            <TextInput
              id="auth-phone"
              type="tel"
              inputMode="tel"
              dir="ltr"
              className="text-start"
              placeholder="07701234567"
              value={phone}
              invalid={Boolean(error)}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
            />
          </div>
          {error ? (
            <p role="alert" className="md-body-small flex items-center gap-1 text-error">
              <Icon name="error" size={14} filled />
              {error}
            </p>
          ) : null}
          <Button type="submit" loading={busy} fullWidth trailingIcon="arrow_back">
            أرسل الرمز
          </Button>
        </form>
      ) : (
        <form onSubmit={submitCode} className="grid gap-4" noValidate>
          <p className="md-body-medium text-on-surface-variant">
            أرسلنا رمز تأكيد إلى <bdi className="font-semibold">{normalizePhone(phone)}</bdi>.
          </p>

          {devCode ? (
            <Banner tone="warn" icon="developer_mode" title="وضع تجريبي">
              قناة الرسائل تجريبية، فالرمز هنا مباشرة:{" "}
              <bdi className="md-title-medium tracking-widest">{devCode}</bdi>
            </Banner>
          ) : null}

          <div>
            <label htmlFor="auth-code" className="md-title-small mb-1 block">
              رمز التأكيد
            </label>
            <TextInput
              id="auth-code"
              ref={codeRef}
              inputMode="numeric"
              dir="ltr"
              className="text-center tracking-[0.5em] md-headline-small"
              placeholder="––––––"
              value={code}
              invalid={Boolean(error)}
              maxLength={8}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
            />
          </div>

          {error ? (
            <p role="alert" className="md-body-small flex items-center gap-1 text-error">
              <Icon name="error" size={14} filled />
              {error}
            </p>
          ) : null}

          <Button type="submit" loading={busy} fullWidth icon="check">
            تأكيد ودخول
          </Button>
          <button
            type="button"
            className="md-label-large text-on-surface-variant hover:text-primary"
            onClick={() => {
              setStep("phone");
              setError(null);
            }}
          >
            غيّر الرقم
          </button>
        </form>
      )}
    </Dialog>
  );
}
